import Badge from '@mui/material/Badge';
import Link from 'next/link';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import MuiLink from '@mui/material/Link';
import type NextLink from 'next/link';
import { MouseEvent, useRef } from 'react';
import { useRouter } from 'next/router';

import * as menuListItemStyles from './MenuListItem.styles';
import GetIcon from '../../../GetIcon';
import useTrackLinkSource from '../../../../utils/tracking/useTrackLinkSource';
import { ExternalDomainLink, PrivateRoutePath, PublicRoutePath } from '../../../../utils/routes';
import { TrackingLinkEvent } from '../../../../utils/tracking/types';

export interface MenuListItemProps {
  badge?: { count: number; color: string };
  component?: typeof NextLink | typeof Link;
  exact?: boolean;
  hide?: boolean;
  href?: string;
  icon?: string;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  primary?: string;
  to?: PrivateRoutePath | PublicRoutePath | ExternalDomainLink;
  trackingLink?: TrackingLinkEvent;
}

function MenuListItem({
  primary,
  icon,
  onClick: onItemClick,
  trackingLink,
  badge,
  hide,
  to,
  ...rest
}: MenuListItemProps) {
  const { pathname } = useRouter();

  const { classes: badgeClasses } = menuListItemStyles.useBadgeColour({ badgeColour: badge?.color });
  const { classes } = menuListItemStyles.useStyles();
  const { classes: listItemClasses } = menuListItemStyles.useListItem();

  const linkRef = useRef<HTMLDivElement>(null);

  useTrackLinkSource(trackingLink, linkRef);

  if (hide) return null;

  const button = (
    <ListItemButton
      classes={listItemClasses}
      ref={linkRef}
      component={to ? MuiLink : undefined}
      onClick={onItemClick}
      {...rest}
      selected={pathname === to}
    >
      {!!icon && (
        <ListItemIcon classes={{ root: classes.listItemIcon }}>
          <GetIcon icon={icon} dark />
        </ListItemIcon>
      )}
      {!!primary && (
        <ListItemText
          className={classes.listItemTextWrapper}
          primaryTypographyProps={{
            variant: 'h5',
            className: classes.listItemText,
          }}
          primary={primary}
        />
      )}

      {!!badge?.count && (
        <ListItemSecondaryAction classes={{ root: classes.secondaryAction }}>
          <Badge badgeContent={badge.count} classes={badgeClasses} />
        </ListItemSecondaryAction>
      )}
    </ListItemButton>
  );

  if (to) {
    return (
      <Link shallow passHref href={to}>
        {button}
      </Link>
    );
  }

  return button;
}

export default MenuListItem;
